/**
 * sdx-mega-menu.js
 * Desktop: hover-intent mega panel (100ms open / 300ms close delay)
 * Mobile:  slide-out drawer with inline accordions
 * A11y:    aria-expanded, aria-controls, Escape close, focus return
 */
(function () {
  "use strict";

  const DESKTOP_BP = 900;
  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 300;

  function isDesktop() {
    return window.matchMedia(`(min-width: ${DESKTOP_BP + 1}px)`).matches;
  }

  function initHeader(root) {
    if (!root || root.dataset.sdxMegaInit === "1") return;
    root.dataset.sdxMegaInit = "1";

    const items = root.querySelectorAll("[data-sdx-mega-item]");
    const drawer = root.querySelector("[data-sdx-drawer]");
    const drawerToggle = root.querySelector("[data-sdx-drawer-toggle]");
    const drawerClose = root.querySelector("[data-sdx-drawer-close]");
    const drawerBackdrop = root.querySelector("[data-sdx-drawer-backdrop]");
    const lastFocusRef = { el: null };

    /* ---------- Desktop hover-intent ---------- */
    let openItem = null;
    let openTimer = null;
    let closeTimer = null;

    function clearTimers() {
      if (openTimer) { clearTimeout(openTimer); openTimer = null; }
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    }

    function closeAll() {
      items.forEach((it) => {
        it.classList.remove("is-open");
        const trigger = it.querySelector("[data-sdx-mega-trigger]");
        if (trigger) trigger.setAttribute("aria-expanded", "false");
      });
      openItem = null;
    }

    function openOnly(item) {
      if (openItem === item) return;
      closeAll();
      item.classList.add("is-open");
      const trigger = item.querySelector("[data-sdx-mega-trigger]");
      if (trigger) trigger.setAttribute("aria-expanded", "true");
      openItem = item;
    }

    items.forEach((item) => {
      const panel = item.querySelector("[data-sdx-mega-panel]");
      if (!panel) return;
      const trigger = item.querySelector("[data-sdx-mega-trigger]");

      item.addEventListener("mouseenter", () => {
        if (!isDesktop()) return;
        clearTimers();
        openTimer = setTimeout(() => openOnly(item), OPEN_DELAY);
      });

      item.addEventListener("mouseleave", () => {
        if (!isDesktop()) return;
        clearTimers();
        closeTimer = setTimeout(() => {
          if (openItem === item) closeAll();
        }, CLOSE_DELAY);
      });

      if (trigger) {
        trigger.addEventListener("focus", () => {
          if (!isDesktop()) return;
          clearTimers();
          openOnly(item);
        });

        // Touch / click toggle (tablet + desktop click)
        trigger.addEventListener("click", (e) => {
          if (!isDesktop()) return;
          if (openItem !== item) {
            e.preventDefault();
            clearTimers();
            openOnly(item);
          }
        });
      }

      // Keep open when focus inside panel
      panel.addEventListener("focusin", () => {
        if (!isDesktop()) return;
        clearTimers();
        openOnly(item);
      });
    });

    // Close when focus leaves whole nav
    const nav = root.querySelector("[data-sdx-nav]");
    if (nav) {
      nav.addEventListener("focusout", (e) => {
        if (!isDesktop()) return;
        if (!nav.contains(e.relatedTarget)) closeAll();
      });
    }

    // Escape closes any open desktop panel
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (isDesktop() && openItem) {
        const trigger = openItem.querySelector("[data-sdx-mega-trigger]");
        closeAll();
        if (trigger) trigger.focus();
      } else if (!isDesktop() && drawer && drawer.classList.contains("is-open")) {
        closeDrawer();
      }
    });

    /* ---------- Mobile drawer ---------- */
    function openDrawer() {
      if (!drawer) return;
      lastFocusRef.el = document.activeElement;
      drawer.classList.add("is-open");
      document.body.classList.add("sdx-no-scroll");
      if (drawerToggle) drawerToggle.setAttribute("aria-expanded", "true");
      const firstLink = drawer.querySelector("a, button");
      if (firstLink) firstLink.focus({ preventScroll: true });
    }

    function closeDrawer() {
      if (!drawer) return;
      drawer.classList.remove("is-open");
      document.body.classList.remove("sdx-no-scroll");
      if (drawerToggle) drawerToggle.setAttribute("aria-expanded", "false");
      // Collapse all nested accordions
      drawer.querySelectorAll("[data-sdx-acc]").forEach((acc) => {
        acc.classList.remove("is-open");
        const btn = acc.querySelector("[data-sdx-acc-btn]");
        if (btn) btn.setAttribute("aria-expanded", "false");
      });
      if (lastFocusRef.el) lastFocusRef.el.focus({ preventScroll: true });
    }

    if (drawerToggle) drawerToggle.addEventListener("click", openDrawer);
    if (drawerClose) drawerClose.addEventListener("click", closeDrawer);
    if (drawerBackdrop) drawerBackdrop.addEventListener("click", closeDrawer);

    // Mobile accordion: first tap expands, if already open and href exists allow navigate
    root.querySelectorAll("[data-sdx-acc-btn]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const acc = btn.closest("[data-sdx-acc]");
        if (!acc) return;
        if (!acc.classList.contains("is-open")) {
          e.preventDefault();
          // Close sibling accordions at same level
          const parent = acc.parentElement;
          if (parent) {
            parent.querySelectorAll(":scope > [data-sdx-acc].is-open").forEach((sib) => {
              if (sib !== acc) {
                sib.classList.remove("is-open");
                const sbtn = sib.querySelector("[data-sdx-acc-btn]");
                if (sbtn) sbtn.setAttribute("aria-expanded", "false");
              }
            });
          }
          acc.classList.add("is-open");
          btn.setAttribute("aria-expanded", "true");
        }
        // second click -> browser follows href (if anchor)
      });
    });

    // Close desktop panels when switching to mobile viewport
    let lastDesktop = isDesktop();
    window.addEventListener("resize", () => {
      const nowDesktop = isDesktop();
      if (nowDesktop !== lastDesktop) {
        closeAll();
        if (drawer) closeDrawer();
        lastDesktop = nowDesktop;
      }
    });
  }

  function bootAll() {
    document.querySelectorAll("[data-sdx-header]").forEach(initHeader);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootAll);
  } else {
    bootAll();
  }

  // Shopify theme editor reload support
  document.addEventListener("shopify:section:load", bootAll);
})();
